import {
  Component,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
  ChangeDetectionStrategy
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { MatTableModule } from '@angular/material/table';
import { Observable, Subject, takeUntil } from 'rxjs';
import {
	PromiseToPay,
	PromiseToPayPermissions,
	PromiseToPayService,
} from '@lmc-app/core/services/promise-to-pay.service';
import { LoanMetaService } from '@lmc-app/core/services/loan-meta.service';
import {
	ARRANGEMENT_TYPE_LABELS,
	PAYMENT_METHOD_LABELS,
	PROMISE_TO_PAY_CANCELLABLE_STATUSES,
	PromiseToPayStatus,
	PromiseToPayType,
} from '@lmc-app/shared/enum/promise-to-pay-status.enum';
import { LmcDialogComponent } from '@lmc-app/shared/components/dialog/lmc-dialog.component';
import { FormatUtil } from '@lmc-app/shared/utils/format.util';
import {
	PromiseToPayDetailDialogComponent,
} from '@lmc-app/loan-servicing/promise-to-pay/detail/promise-to-pay-detail-dialog.component';
import {
	PromiseToPayDialogComponent,
	PromiseToPayDialogResult,
} from '@lmc-app/loan-servicing/promise-to-pay/create/promise-to-pay-dialog.component';
import { PayoffContext } from '@lmc-app/loan-servicing/promise-to-pay/create/payoff-statement';
import {
	canEditPromise,
	editDialogData,
} from '@lmc-app/loan-servicing/promise-to-pay/create/promise-to-pay-edit';

/**
 * One loan's promises and arrangements, with the create action beside them — the
 * Promise to Pay tab on the loan page. The Create menu picks PTP or arrangement.
 *
 * Row actions are History, Edit and Cancel; everything else is done from Loan Servicing.
 */
@Component({
	selector: 'app-promise-to-pay-panel',
	standalone: true,
	imports: [
		CommonModule,
		MatButtonModule,
		MatIconModule,
		MatMenuModule,
		MatProgressSpinnerModule,
		MatSnackBarModule,
		MatTableModule,
	],
	templateUrl: './promise-to-pay-panel.component.html',
	changeDetection: ChangeDetectionStrategy.Eager,
	styleUrls: ['./promise-to-pay-panel.component.scss'],
})
export class PromiseToPayPanelComponent implements OnInit, OnChanges, OnDestroy {
	@Input() acctrefno!: number;
	@Input() loanNumber = '';
	@Input() customerName = '';
	/** Loan page meta: insurance, NSF fees and origination date for the payoff quote. */
	@Input() meta: { insurance?: number; eff_fees_balance?: number; origination_date?: string } | null = null;
	@Input() loanGroupNo: number | null = null;

	readonly FormatUtil = FormatUtil;
	readonly PromiseToPayStatus = PromiseToPayStatus;
	readonly PromiseToPayType = PromiseToPayType;

	readonly displayedColumns = [
		'actions',
		'promiseDate',
		'type',
		'paymentMethod',
		'amount',
		'status',
		'owner',
	];

	rows: PromiseToPay[] = [];
	isLoading = false;
	/** Set in Admin Settings → Promise to Pay. Nothing extra is granted until it loads. */
	private permissions: PromiseToPayPermissions | null = null;

	private readonly busyIds = new Set<number>();
	private readonly destroy$ = new Subject<void>();

	constructor(
		private readonly promiseToPayService: PromiseToPayService,
		private readonly loanMetaService: LoanMetaService,
		private readonly dialog: MatDialog,
		private readonly snackBar: MatSnackBar
	) {}

	ngOnInit(): void {
		this.load();
		this.promiseToPayService
			.getPermissions()
			.pipe(takeUntil(this.destroy$))
			.subscribe({
				next: (permissions) => (this.permissions = permissions),
				// Without them the panel works as for an employee with none.
				error: () => undefined,
			});
	}

	ngOnChanges(changes: SimpleChanges): void {
		if (changes['acctrefno'] && !changes['acctrefno'].firstChange) {
			this.load();
		}
	}

	ngOnDestroy(): void {
		this.destroy$.next();
		this.destroy$.complete();
	}

	private load(): void {
		if (!this.acctrefno) return;
		this.isLoading = true;
		this.promiseToPayService
			.listForLoan(this.acctrefno)
			.pipe(takeUntil(this.destroy$))
			.subscribe({
				next: (promises) => {
					this.rows = promises;
					this.isLoading = false;
				},
				error: (err) => {
					this.isLoading = false;
					this.showError(err, 'Failed to load promises for this loan.');
				},
			});
	}

	trackById(_index: number, row: PromiseToPay): number {
		return row.id;
	}

	isBusy(row: PromiseToPay): boolean {
		return this.busyIds.has(row.id);
	}

	typeLabel(row: PromiseToPay): string {
		if (row.type !== PromiseToPayType.Arrangement) return 'Promise to Pay';
		return row.arrangementType
			? ARRANGEMENT_TYPE_LABELS[row.arrangementType]
			: 'Arrangement';
	}

	paymentMethodLabel(row: PromiseToPay): string {
		return PAYMENT_METHOD_LABELS[row.paymentMethod] ?? row.paymentMethod;
	}

	canEdit(row: PromiseToPay): boolean {
		return canEditPromise(this.permissions);
	}

	canCancel(row: PromiseToPay): boolean {
		return PROMISE_TO_PAY_CANCELLABLE_STATUSES.includes(row.status);
	}

	onCreate(type: PromiseToPayType): void {
		this.dialog
			.open(PromiseToPayDialogComponent, {
				width: '640px',
				autoFocus: false,
				data: {
					acctrefno: this.acctrefno,
					loanNumber: this.loanNumber,
					customerName: this.customerName,
					type,
					payoff: this.payoffContext(),
					canBackdate: !!this.permissions?.canBackdate,
				},
			})
			.afterClosed()
			.pipe(takeUntil(this.destroy$))
			.subscribe((saved: PromiseToPayDialogResult) => {
				if (!saved) return;
				// A new row cannot be patched into a list it was never in.
				this.load();
				this.notifyLoanMeta();
			});
	}

	onEdit(row: PromiseToPay): void {
		this.dialog
			.open(PromiseToPayDialogComponent, {
				width: '640px',
				autoFocus: false,
				data: editDialogData(row, this.permissions, this.payoffContext()),
			})
			.afterClosed()
			.pipe(takeUntil(this.destroy$))
			.subscribe((saved: PromiseToPayDialogResult) => {
				if (!saved) return;
				this.patchRow(saved);
				this.notifyLoanMeta();
			});
	}

	private payoffContext(): PayoffContext {
		return {
			loanGroupNo: this.loanGroupNo,
			insurance: this.meta?.insurance ?? 0,
			nsfFees: this.meta?.eff_fees_balance ?? 0,
			originationDate: this.meta?.origination_date ?? null,
		};
	}

	/** The promise's facts and its full history. Read-only. */
	onViewHistory(row: PromiseToPay): void {
		this.dialog.open(PromiseToPayDetailDialogComponent, {
			width: '640px',
			autoFocus: false,
			data: { promise: row },
		});
	}

	onCancel(row: PromiseToPay): void {
		this.confirm(
			'Cancel promise',
			`Cancel the ${this.typeLabel(row)} of ${FormatUtil.formatCurrency(
				row.amount
			)} due ${row.promiseDate}?`,
			() =>
				this.runAction(
					row,
					this.promiseToPayService.cancel(row.id),
					'Cancelled'
				)
		);
	}

	private runAction(
		row: PromiseToPay,
		request$: Observable<PromiseToPay>,
		pastTense: string
	): void {
		this.busyIds.add(row.id);
		request$.pipe(takeUntil(this.destroy$)).subscribe({
			next: (updated) => {
				this.busyIds.delete(row.id);
				this.patchRow(updated);
				this.notifyLoanMeta();
				this.snackBar.open(`${pastTense}.`, 'Dismiss', { duration: 3000 });
			},
			error: (err) => {
				this.busyIds.delete(row.id);
				this.showError(err, `Failed to ${pastTense.toLowerCase()}.`);
			},
		});
	}

	/**
	 * Cancelled and Deleted promises leave this list — the loan feed excludes them — so
	 * those reload; anything else is patched in place.
	 */
	private patchRow(updated: PromiseToPay): void {
		const leavesTheList =
			updated.status === PromiseToPayStatus.Cancelled ||
			updated.status === PromiseToPayStatus.Deleted;
		if (leavesTheList) {
			this.rows = this.rows.filter((row) => row.id !== updated.id);
			return;
		}
		this.rows = this.rows.map((row) =>
			row.id === updated.id ? updated : row
		);
	}

	/** Other tabs on the loan page show promises too; keep them in step. */
	private notifyLoanMeta(): void {
		this.loanMetaService.triggerAction(false, 'RELOAD_ONLY', {
			acctrefno: this.acctrefno,
		});
	}

	private confirm(title: string, content: string, onConfirm: () => void): void {
		this.dialog
			.open(LmcDialogComponent, {
				data: {
					title,
					content,
					positiveButtonText: 'Confirm',
					negativeButtonText: 'Cancel',
				},
			})
			.afterClosed()
			.pipe(takeUntil(this.destroy$))
			.subscribe((confirmed) => {
				if (confirmed) onConfirm();
			});
	}

	private showError(err: unknown, fallback: string): void {
		const body = (err as { error?: { message?: string } })?.error;
		this.snackBar.open(body?.message ?? fallback, 'Dismiss', {
			duration: 6000,
		});
	}
}
